import { AiChangeResourceType } from '@module/ai';
import {
  EssayQuestion,
  FillInBlankQuestion,
  Heading,
  MatchingQuestion,
  MultipleChoiceQuestion,
  Question,
  QuestionProps,
  QuestionType,
  Section,
  SingleChoiceQuestion,
} from '@module/form';

export type AiEntity = Question<QuestionProps> | Section | Heading;

export interface AiJsonToEntityConverterInput {
  itemType: AiChangeResourceType;
  json: string;
}

/**
 * Convert persisted JSON props into domain entities.
 * Used when loading AI resource change records from storage.
 */
export class AiJsonToEntityConverter {
  public static execute(input: AiJsonToEntityConverterInput): AiEntity | undefined {
    const { itemType, json } = input;

    let props: Record<string, unknown>;
    try {
      props = JSON.parse(json) as Record<string, unknown>;
    } catch (error) {
      return undefined;
    }

    switch (itemType) {
      case AiChangeResourceType.Question:
        return this.createQuestionFromProps(props);
      case AiChangeResourceType.Section:
        return this.createSectionFromProps(props);
      case AiChangeResourceType.Heading:
        return this.createHeadingFromProps(props);
      default:
        return undefined;
    }
  }

  private static createQuestionFromProps(
    data: Record<string, unknown>,
  ): Question<QuestionProps> | undefined {
    const type = data.type as QuestionType | undefined;

    try {
      switch (type) {
        case QuestionType.SingleChoice: {
          const normalized = AiJsonToEntityConverter.normalizeQuestionJson(data);
          const result = SingleChoiceQuestion.fromJson(normalized);

          return result.isSuccess ? result.getValue() : undefined;
        }
        case QuestionType.MultipleChoice: {
          const normalized = AiJsonToEntityConverter.normalizeQuestionJson(data);
          const result = MultipleChoiceQuestion.fromJson(normalized);

          return result.isSuccess ? result.getValue() : undefined;
        }
        case QuestionType.FillInBlank: {
          const normalized = AiJsonToEntityConverter.normalizeQuestionJson(data);
          const result = FillInBlankQuestion.fromJson(normalized);

          return result.isSuccess ? result.getValue() : undefined;
        }
        case QuestionType.Matching: {
          const normalized = AiJsonToEntityConverter.normalizeQuestionJson(data);
          const result = MatchingQuestion.fromJson(normalized);

          return result.isSuccess ? result.getValue() : undefined;
        }
        case QuestionType.Essay:
        default: {
          const normalized = AiJsonToEntityConverter.normalizeQuestionJson(data);
          const result = EssayQuestion.fromJson(normalized);

          return result.isSuccess ? result.getValue() : undefined;
        }
      }
    } catch {
      return undefined;
    }
  }

  private static normalizeQuestionJson(data: Record<string, unknown>): Record<string, unknown> {
    return {
      ...data,
      files: (data.files as unknown[]) ?? [],
      previousQuestionId: (data as any).previousQuestionId ?? 'none',
    };
  }

  private static createSectionFromProps(data: Record<string, unknown>): Section | undefined {
    try {
      return Section.create(data as any).getValue();
    } catch {
      return undefined;
    }
  }

  private static createHeadingFromProps(data: Record<string, unknown>): Heading | undefined {
    try {
      const id = (data.id as string | undefined) ?? undefined;
      return Heading.create(data as any, id).getValue();
    } catch {
      return undefined;
    }
  }
}
